import React, { useMemo } from 'react';
import { Form, Tab, Popup, Icon } from 'semantic-ui-react';
import { useTextResources, useMessageResources } from '../../language';
import { RequiredGtdTypes, RequiredGtdTypeEnum } from '../shared/constants';
import { TimePicker } from '../shared/DatePicker';

/**
 * Guarantee Policy pane for the CancellationAndDepositPoliciesPopup.
 * Unlike cancellation/deposit which are arrays of sub-policies,
 * guarantee is a single configuration on the reservation policy itself.
 *
 * Props:
 *  - requiredGtdType (int)          – current RequiredGtdType value
 *  - guaranteedTime  (string|null)  – HH:mm local time string
 *  - onGtdTypeChange(value)         – called when dropdown changes
 *  - onGuaranteedTimeChange(value)  – called when time input changes
 */
const formatEtaAsTime = (eta) => {
  if (!eta) return '14:00';
  const h = String(eta.Hours ?? 14).padStart(2, '0');
  const m = String(eta.Minutes ?? 0).padStart(2, '0');
  return `${h}:${m}`;
};

const GuaranteePolicyPane = ({
  requiredGtdType,
  guaranteedTime,
  onGtdTypeChange,
  onGuaranteedTimeChange,
  hotelEta,
}) => {
  const text = useTextResources();
  const message = useMessageResources();

  const gtdTypeOptions = useMemo(
    () =>
      RequiredGtdTypes.map((t) => ({
        key: t.Id,
        value: t.Id,
        text: text(t.Name),
      })),
    [text]
  );

  const isGuaranteeRequired = requiredGtdType !== RequiredGtdTypeEnum.NoGuaranteeRequired;

  return (
    <Tab.Pane style={{ border: 'none', boxShadow: 'none', padding: '1em 0' }}>
      <Form>
        <Form.Group widths={2}>
          <Form.Dropdown
            label={text('RequiredGuaranteeType')}
            selection
            options={gtdTypeOptions}
            value={requiredGtdType ?? RequiredGtdTypeEnum.NoGuaranteeRequired}
            onChange={(e, { value }) => onGtdTypeChange(value)}
            fluid
          />
          <Form.Field>
            <label>
              {text('GuaranteedTime')}{' '}
              <Popup
                content={message('GuaranteedTimeTooltip')}
                trigger={<Icon name="info circle" size="small" color="grey" />}
                position="top center"
                size="small"
              />
            </label>
            <TimePicker
              value={guaranteedTime || formatEtaAsTime(hotelEta)}
              onChange={onGuaranteedTimeChange}
              disabled={!isGuaranteeRequired}
              clearable={false}
            />
          </Form.Field>
        </Form.Group>
      </Form>
    </Tab.Pane>
  );
};

export default GuaranteePolicyPane;
